/* assets/price-tier.js — the price-tier test's runtime: writes the dealt arm
 * into the cart, rewrites card prices, and reports the arm to the beacon.
 *
 * THE CONTRACT (three parties, one sessionStorage key 'mf_pt'):
 *  - layout/theme.liquid's inline deal assigns the arm and exposes
 *    window.__mfPT = { tier, off } synchronously - product-rhode's shownPrice
 *    reads it for every PDP price;
 *  - this file POSTs the tier into the cart attribute `mf_pt` (BEFORE the
 *    first add in practice: it fires at DOMContentLoaded, adds need a human),
 *    and rewrites [data-pt] card prices (snippets/dcard.liquid marks them
 *    server-side with exact cents, all-cases members only);
 *  - the cart transform function (mf-price-test app) charges catalog - off
 *    for every all-cases line whenever the attribute holds a whitelisted arm.
 *  Shown price and charged price therefore always move together; with JS off
 *  neither moves and the catalog price is both shown and charged.
 *
 * Measurement: price:t_<arm> lands once per pageview through the mf_nav_step
 * channel, so step_add/step_chk joins price every arm with zero server work.
 */
(function () {
  'use strict';
  var D = document, W = window;

  function tier() { return (W.__mfPT && W.__mfPT.tier) || ''; }
  function p22() { return W.__mfP22 === 'on' ? 'on' : 'off'; }
  function p11() { return W.__mfP11 === 'on' ? 'on' : 'off'; }
  function off() { return (W.__mfPT && W.__mfPT.off) || 0; }
  /* BF depth test: percentage case arms. The display rounds exactly like the
     cart transform - Math.round(unitRON * (100-pct) / 100) - so face and
     charge agree to the leu. */
  function pct() { return (W.__mfPT && W.__mfPT.pct) || 0; }
  /* BF v3 grid: the percent actually CHARGED (the cart transform's PCT) and
     the arm's group - 'a' anchors on the real catalog, 'b' on a derived
     anchor. Both dealt by theme.liquid; both re-adopted from the cart. */
  function charge() { return (W.__mfPT && W.__mfPT.charge) || 0; }
  /* v5 flat-target arms: the RON price the shopper should end up paying, 0 when
     the arm is a pure percentage. Mirrors FLAT_RON in the cart transform. */
  function flat() { return (W.__mfPT && W.__mfPT.flat) || 0; }
  /* GRID ORDER x BADGE TEST (2026-09-12): the coins live on window.__mfGO, dealt
     in layout/theme.liquid. Reported from HERE, not from the PLP, because this
     file loads on every page - so the arm lands on the PDP pageview that carries
     case_add and inherits the step_add join. The old rot: arm was pushed only
     from collection-v2 and had an EMPTY join for three weeks; that is the
     mistake this fixes. 'ns' = the session never saw an armed grid: it stays in
     the denominator as its own bucket instead of leaking into control. */
  function goa() {
    var a = (W.__mfGO || {}).arm;
    if (a !== 'o0' && a !== 'o1') { return ''; }
    var seen = '';
    try { seen = sessionStorage.getItem('mf_goseen') || ''; } catch (e) {}
    return seen === '1' ? a : 'ns';
  }
  function gob() {
    var b = (W.__mfGO || {}).badge;
    return (b === 'b0' || b === 'b1') ? b : '';
  }
  function grp() { return (W.__mfPT && W.__mfPT.grp) || ''; }
  /* presentation axis: same charged price, differently dressed */
  function ptd() { return W.__mfPTD === 'sale' ? 'sale' : 'plain'; }

  /* the same convention as product-rhode's money(): whole RON stays whole */
  function fmt(cents) {
    var v = cents / 100;
    return ((v % 1 === 0) ? v.toFixed(0) : v.toFixed(2).replace('.', ',')) + ' RON';
  }

  /* ── card prices: rewrite every server-marked [data-pt] node. The original
     cents ride the attribute, so a re-decorate after an arm change (adoption)
     recomputes from source - including BACK to catalog when z0 is adopted. ── */
  /* the 2+2 CARD badge is merchant data (tag setting), so it is found by
     text, not class - only ever in the off arm, only display, never data */
  function sweepP22Badges() {
    if (p22() !== 'off') { return; }
    var b = D.querySelectorAll('.tag-badge, .dbadge');
    for (var i = 0; i < b.length; i++) {
      if (b[i].textContent.indexOf('2+2') > -1) { b[i].style.display = 'none'; }
    }
  }
  var saleCssDone = false;
  function ensureSaleCss() {
    if (saleCssDone) { return; }
    saleCssDone = true;
    var st = D.createElement('style');
    /* the display rule outguns dcard.css's mobile '.dcard__price s
       { display: none }' (0,1,1) by specificity (0,2,1) - without it the
       whole sale treatment silently vanishes on phones. The now-price and
       the percent pill speak the PDP hero's language (user 2026-08-25):
       accent price, small accent pill right beside it. */
    st.textContent = '.mfsale-was{opacity:.55;font-weight:400;margin-right:.35em}' +
      '.dcard__price s.mfsale-was{display:inline}' +
      '.mfsale-now{color:var(--accent,#8F00FF);font-weight:700}' +
      '.mfsale-off{display:inline-block;margin-left:.4em;padding:1px 6px;border-radius:999px;' +
        'background:var(--accent,#8F00FF);color:#fff;font-size:.72em;font-weight:700;vertical-align:1px}';
    (D.head || D.documentElement).appendChild(st);
  }
  /* one node, two dressings: plain = the effective price alone; sale = the
     CATALOG cents struck through beside it. fmt() output only - no user data
     ever reaches innerHTML. */
  /* A server-rendered sibling <s> (real merchant compare_at on dcards)
     already dresses the card as a markdown - injecting a second strike would
     stack two crossed-out prices, so we stand down there. */
  /* BF FRAMING TEST v2 (2026-09-03): every percentage arm CHARGES the same -
     catalog minus CHARGE_PCT, the cart transform's exact formula - and the
     arm's own 50/60/70 is now only a BADGE. The badge rides a DERIVED anchor,
     anchor = eff / (1 - badge/100), so the struck price differs per arm while
     the price paid does not: one 348 RON case reads 208/260/347 struck and
     104 charged in p50/p60/p70 alike. That isolates the anchor+badge framing
     at an identical effective price - v1 varied the real price too, which is
     why its "-70% wins" could not be read as a framing result.
     A badge of 0 (the protector axis) keeps the catalog as its own anchor. */
  /* BF v3 (2026-09-04): the arm carries its own CHARGE percent (A: the badge
     itself, B: always the -70% level) and its anchor rule - group A strikes
     the real catalog, group B a derived anchor rounded UP to whole 10 RON and
     capped at the catalog. Mirrors product-rhode's ptAnchor and the drawer's
     Liquid, so every surface tells one story per arm. */
  /* group B anchor (v4, 2026-09-07, user decision "ne 51 legyen"): charge /
     (1 - badge) rounded to the NEAREST whole RON, nudged by a leu or two so
     the rendered Math.round((1 - eff / anchor) * 100) is EXACTLY the badge.
     v3 rounded UP to whole 10 RON and read -51/-61% under a -50/-60% band.
     Cents in, cents out. Identical helper in product-rhode; the drawer's
     Liquid mirrors it with integer arithmetic. */
  function bfAnchor(eff, badge) {
    var a = Math.round((eff / 100) / ((100 - badge) / 100)) * 100;
    var tries = [a, a + 100, a - 100, a + 200, a - 200];
    for (var i = 0; i < tries.length; i++) {
      if (tries[i] > eff && Math.round((1 - eff / tries[i]) * 100) === badge) { return tries[i]; }
    }
    return a;
  }
  /* 2026-09-21: the charge is PASSED IN (decorate: charge() for cases, 0 for the
     protector axis) instead of gated on badge > 0 - a badge-less arm (c25, pay
     catalogue x 0.75 shown plain) would otherwise print the full catalogue. The
     derived anchor and the sale dressing now need a badge, so a badge-less arm
     can never render a struck price or a '-0%' / '-25%' pill. */
  function priceInto(node, cents, offv, badge, cp) {
    var eff = cents > offv ? cents - offv : cents;
    var anchor = cents;
    var fl = flat() * 100;
    if (cp > 0) {
      var pcEff = Math.round((cents / 100) * (100 - cp) / 100) * 100;
      /* v5: the flat target wins when the arm has one AND it is the cheaper of
         the two - the cart transform's floor rule to the leu. When it does NOT
         bind (a cheap tier whose badge percentage is already below the target)
         the arm degrades to a plain percentage and the catalog is its anchor,
         which is what keeps the rendered percent equal to the badge there. */
      var flatBound = fl > 0 && fl < pcEff;
      if (flatBound) { pcEff = fl; }
      if (pcEff > 0 && pcEff < cents) {
        eff = pcEff;
        /* a badge equal to the charge (B70/q70) keeps the catalog anchor, so
           those arms strike the untouched original; a lower badge - or any
           bound flat target - derives its own, capped at the catalog */
        if (badge > 0 && grp() === 'b' && (flatBound || badge < cp)) {
          var a = bfAnchor(pcEff, badge);
          anchor = a > cents ? cents : (a > eff ? a : cents);
        }
      }
    }
    var sib = node.previousElementSibling;
    var hasServerStrike = !!(sib && sib.tagName === 'S');
    /* full-price mode (window.__mfFP): the card shows the price it charges, never a struck anchor or -NN% */
    if (!W.__mfFP && ptd() === 'sale' && badge > 0 && eff < anchor && !hasServerStrike) {
      ensureSaleCss();
      node.innerHTML = '<s class="mfsale-was">' + fmt(anchor) + '</s>' +
        '<span class="mfsale-now">' + fmt(eff) + '</span>' +
        '<span class="mfsale-off">-' + Math.round((1 - eff / anchor) * 100) + '%</span>';
    } else {
      node.textContent = fmt(eff);
    }
  }
  function decorate() {
    sweepP22Badges();
    var nodes = D.querySelectorAll('[data-pt]:not([data-pt-done])');
    for (var i = 0; i < nodes.length; i++) {
      var cents = parseInt(nodes[i].getAttribute('data-pt'), 10);
      if (isFinite(cents)) { priceInto(nodes[i], cents, off(), pct(), charge()); }
      nodes[i].setAttribute('data-pt-done', '1');
    }
    /* the protector axis decorates its own marks with its own offset */
    var pnodes = D.querySelectorAll('[data-ptp]:not([data-ptp-done])');
    for (var j = 0; j < pnodes.length; j++) {
      var pcents = parseInt(pnodes[j].getAttribute('data-ptp'), 10);
      if (isFinite(pcents)) { priceInto(pnodes[j], pcents, poff(), 0, 0); }
      pnodes[j].setAttribute('data-ptp-done', '1');
    }
  }

  var ARMS_OFF = { z0: 0 }; /* 2026-09-30: every price arm stopped (theme.liquid V3) */
  /* BF v3 grid - c: percent charged, b: badge, g: group. p50/p60/p70 retired
     2026-09-04, kept so a cart opened under them keeps its face. */
  /* 2026-09-30 (user): EMPTY - every price arm is stopped (the catalogue carries the sale
     itself now). No cart is adopted, so a cart holding q72 / c25 items is rewritten to z0 and
     re-rendered at the catalogue price. MUST stay equal to the dealer's V3 in theme.liquid. */
  var ARMS_V3 = {};
  var PARMS_OFF = { p0: 0, p20: 2000, p40: 4000 };
  function ptier() { return (W.__mfPTP || {}).tier || ''; }
  function poff() { return (W.__mfPTP || {}).off || 0; }
  function adoptPArm(t2) {
    try { localStorage.setItem('mf_ptp3', JSON.stringify({ t: t2, e: new Date().getTime() + 6048e5 })); } catch (e) {}
    W.__mfPTP = { tier: t2, off: PARMS_OFF[t2] };
    var done = D.querySelectorAll('[data-ptp-done]');
    for (var i = 0; i < done.length; i++) { done[i].removeAttribute('data-ptp-done'); }
    decorate();
  }

  /* Adopt an arm the CART already carries: localStorage, __mfPT and the card
     prices all follow. Already-rendered PDP prices converge on the next
     interaction and only ever OVERSTATE meanwhile - the safe direction. */
  function adoptArm(t) {
    /* v5: the dealer reads mf_pt8, so writing a stale key here meant an adopted arm
       was never persisted and every pageview re-dealt then re-adopted.
       2026-09-21: the dealer's key is now mf_pt9 - the two MUST move together. */
    try { sessionStorage.setItem('mf_pt9', JSON.stringify({ t: t })); } catch (e) {}
    var v3 = ARMS_V3[t] || null;
    W.__mfPT = { tier: t, off: ARMS_OFF[t] || 0,
      pct: v3 ? v3.b : 0, charge: v3 ? v3.c : 0, grp: v3 ? v3.g : '',
      /* v5: without `flat` the rebuilt object lost the target, and an adopted
         q50 cart would have painted 50% off the catalog instead of 125 RON. */
      flat: v3 ? (v3.f || 0) : 0,
      /* 2026-10-05: z0 carries the 1+1 while the theme setting mf_p11_on is set - the dealer's exact rule
         (window.__mfP11Z0, layout/theme.liquid), so a late adoption can never strip mf-p11-on */
      p11: ((v3 && v3.p11) || (t === 'z0' && W.__mfP11Z0)) ? 1 : 0 };
    /* 2026-09-21: the 1+1 gate and the dressing belong to the ARM, exactly as the
       dealer derives them - an adopted q72 cart goes 1+1 'off' and 'sale', a c25
       cart 'on' and 'plain'. assertAttribute re-reads both after adopting. */
    W.__mfP11 = W.__mfPT.p11 ? 'on' : 'off';
    W.__mfPTD = W.__mfPT.pct > 0 ? 'sale' : 'plain';
    /* the announcement band follows the html class */
    try {
      var h = D.documentElement;
      h.className = h.className.replace(/\bmf-bf-[a-z0-9]+\b/g, '').replace(/\bmf-p11-on\b/g, '').replace(/\s+/g, ' ');
      /* review 2026-09-13: the strip regex also eats mf-bf-on and mf-bf-badgeNN, and
         only mf-bf-<arm> was put back - so an adopted cart lost the announcement band
         (theme.liquid gates it on mf-bf-on) AND the 50px fixed-header offset. Mirror
         the dealer's class stamp exactly: the BF classes only for an arm WITH a badge
         (a badge-less arm would get an empty grey band), mf-p11-on only for a p11 arm. */
      /* full-price mode (window.__mfFP, layout/theme.liquid): no percentage band on a late adoption either */
      if (v3 && v3.b > 0 && !W.__mfFP) { h.className += ' mf-bf-' + t + ' mf-bf-on mf-bf-badge' + v3.b; }
      if (W.__mfP11 === 'on') { h.className += ' mf-p11-on'; }
    } catch (e1) {}
    var done = D.querySelectorAll('[data-pt-done]');
    for (var i = 0; i < done.length; i++) { done[i].removeAttribute('data-pt-done'); }
    decorate();
    /* the rhode PDP renders its own price surfaces - re-render on the new arm */
    try { D.dispatchEvent(new CustomEvent('mf:pt-adopted')); } catch (e2) {}
  }

  /* ── the cart carries the arm: read-first, and THE CHEAPER ARM WINS.
     The cart attribute is shared (tabs, browsers, a logged-in cart); this
     browser's deal is only one voice. If the cart already holds a cheaper
     valid arm, adopt it - every stale surface then overstates, which is the
     safe direction; overwriting upward would undercut whatever the other
     context displayed. Only when OUR arm is the cheaper one do we write, and
     a genuine re-price of a non-empty cart re-renders the drawer so it stops
     advertising prices the transform no longer charges. Failures retry a few
     times instead of trusting a write that never landed. ── */
  var lastPost = 0;
  var retries = 0;
  function scheduleRetry() {
    lastPost = 0;
    if (retries++ < 3) { setTimeout(assertAttribute, 4100); }
  }
  /* The ON-wins adoption died with the 2+2 node (deactivated 2026-08-21):
     resurrecting the promo UI from a stale cart attribute would ADVERTISE a
     discount the server no longer grants - the exact lie the old rule
     existed to prevent. Stale 'on' carts now get overwritten to 'off'. */
  /* the ORDER books on the cart's mf_ptd, so the shown dressing must follow
     it: adopt whatever valid value the cart already carries */
  function adoptPtd(dv) {
    try { localStorage.setItem('mf_ptd1', JSON.stringify({ d: dv, e: new Date().getTime() + 6048e5 })); } catch (e) {}
    W.__mfPTD = dv;
    var done = D.querySelectorAll('[data-pt-done], [data-ptp-done]');
    for (var i = 0; i < done.length; i++) {
      done[i].removeAttribute('data-pt-done');
      done[i].removeAttribute('data-ptp-done');
    }
    decorate();
    /* the rhode PDP renders its own price surfaces - tell it the dressing
       changed so the page does not show two arms at once */
    try { D.dispatchEvent(new CustomEvent('mf:ptd-adopted')); } catch (e) {}
  }
  /* 2026-10-05 (verifier race): ONE CART WRITER AT A TIME. At page load the mystery gift reconciler
     (snippets/mf-gift-one.liquid) re-paired a gift while this write was in flight, and two cart writes in
     flight together can undo each other - the cart kept mf_mg 'c' in 1 of 2 runs and the gift cost 19 RON
     until the next page. The read and the write below now run as ONE job in the shared window.mfCartLock
     (defined by that snippet, which runs first), so neither lands in the middle of the other and the read
     always sees the cart the previous job left. Without the snippet the job runs unlocked, as before. */
  function locked(job) {
    return W.mfCartLock ? W.mfCartLock(job) : Promise.resolve().then(job);
  }
  /* a write that another cart write undid (theme.js's own adds and quantity links take no lock) is put back
     by ONE trailing check at the end of a collapsed burst instead of waiting for the next pageview. Bounded
     by the writes this pageview made, so a party that keeps reverting the attributes cannot start a loop. */
  var trailing = 0;
  var writes = 0;
  function assertAttribute() {
    var t = tier();
    if (!t) { return; }
    var now = new Date().getTime();
    if (now - lastPost < 4000) { /* collapse bursts of cart:refresh */
      if (!trailing && writes < 5) {
        trailing = setTimeout(function () { trailing = 0; assertAttribute(); }, lastPost + 4000 - now + 50);
      }
      return;
    }
    lastPost = now;
    try {
      return locked(function () { return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' }).then(function (r) { return r.json(); }).then(function (cart) {
        var cur = (cart.attributes || {}).mf_pt || '';
        var curP = (cart.attributes || {}).mf_p22 || '';
        var curPT = (cart.attributes || {}).mf_ptp || '';
        var curD = (cart.attributes || {}).mf_ptd || '';
        var cur11 = (cart.attributes || {}).mf_p11 || '';
        var curGO = (cart.attributes || {}).mf_go || '';
        var curMG = (cart.attributes || {}).mf_mg || '';
        /* 2026-10-09 RO POPUP A/B/C (sections/mf-popup-game.liquid, CONTRACT.md): mf_pga = the popup arm a / b / c for
           EVERY dealt visitor, popup seen or not (intention to treat); pixel v26 books it as order.pga. Written only
           while the test runs - window.__mfPG is absent with the test 'ki', and x (storage failure) / s (already
           subscribed) are excluded and write nothing - so with the test off this write is exactly what it was.
           In both no-op comparisons below, so the cart is rewritten once per arm change, not per pageview.
           2026-10-14 (CONTRACT UPDATE 7, test 'cp'): p = the daily case with percent prizes, written like c. */
        var curPGA = (cart.attributes || {}).mf_pga || '';
        var wantPGA = (W.__mfPG === 'a' || W.__mfPG === 'b' || W.__mfPG === 'c' || W.__mfPG === 'p') ? W.__mfPG : '';
        var okPGA = !wantPGA || curPGA === wantPGA;
        var wantP = p22();
        var wantPT = ptier();
        var wantD = ptd();
        var want11 = p11();
        /* grid test cell for the order read; '' when the coin is x0. Must be in
           the no-op comparison below or the cart is rewritten every pageview. */
        /* 2026-09-22: the grid test is closed, so mf_go now carries the PDP test arm
           (theme.liquid PDPOOL): o0_b1 = A (type cards), o1_b1 = B (Full-Shock only).
           Pixel v20+ whitelists exactly this shape, so orders book per arm with no
           pixel change. Written on every page, so a cart always carries the current arm. */
        /* 2026-10-01: mf_go carries the MYSTERY GIFT test arm (theme.liquid MGPOOL) - A o0_b0, B o1_b0, C ns_b0, all
           shapes pixel v20+ and the beacon already accept - and mf_mg the arm itself, which the promo22-gate discount
           reads (the gift is free on b only). A cart that holds items keeps the arm it was filled under: adopt it,
           so a re-deal never turns a B cart's free gift into a paid one. */
        var MGV = { a: 'o0', b: 'o1', c: 'ns' };
        /* 2026-10-05 (owner): the A/B/C test is over and everyone is arm B (the gift free, one per order). A cart's
           a/c stamp is NOT adopted any more - it is rewritten to b below, together with mf_go o1_b0. */
        var wantMG = 'b';
        /* review: _b0 cells - every pre-release cart already carries o0_b1, which would have booked old carts into A */
        /* 2026-10-07: the C/D PRODUCT-PAGE TEST (Termékoldal = 'cd') takes over mf_go while it runs: arm C o0_b1,
           arm D o1_b1 - shapes pixel v20+ (order.go) and the beacon (rev:go_*) already accept. These cells are NOT
           fresh: the closed 09-22 PDP test wrote them (A = o0_b1, B = o1_b1), o0_b1 was every cart's default after
           its 09-25 close, and the 10-01 gift arms wrote them until the same-day move to _b0 - they have been
           unstamped only since 10-01. A new cell is no alternative: pixel v20+ and the beacon GO_OK accept only the
           o0/o1/ns x b0/b1 shapes, and every one of those has history. So the dashboard floors the C/D order rows at
           2026-10-07 (renderPdpCD CD_START). A stale cart is rewritten on its next storefront pageview, but a
           recovered abandoned checkout (Klaviyo / Recart link straight to /checkouts/...) loads no storefront page,
           so a cart stamped 09-22..10-01 can still book into C or D - a known small residual, said on the card.
           window.__mfPF exists only where theme.liquid's dealer rendered ('cd' mode); everywhere else - 'now', 'c',
           'd', the excluded 'x' and a stale tab opened before the switch - it is the o1_b0 of today, unchanged. mf_go gates NO price: no cart
           Function reads it (verified 2026-10-07 against mf-price-app: they read mf_pt / mf_ptp / mf_mg / mf_p11 /
           caseSku), and wantMG stays 'b' because the promo22-gate Function frees the gift on mf_mg = b. Already in
           both no-op comparisons below, so the cart is rewritten once per arm change, not per pageview. */
        var wantGO = (W.__mfPF === 'c') ? 'o0_b1' : (W.__mfPF === 'd') ? 'o1_b1' : MGV[wantMG] + '_b0';
        /* protector adoption REMOVED with the ladder close (2026-08-27):
           everyone is p0, and adopting a stale p20/p40 from an old cart
           would resurrect a retired discount - the 2+2 lesson again. */
        /* ptd adoption REMOVED at the presentation pause (2026-08-27):
           everyone is 'plain', a stale 'sale' cart attribute gets
           overwritten - display-only, so the overwrite is harmless.
           2026-09-21: the dressing (and the 1+1 gate) now follow the ARM - the
           dealer and adoptArm derive both from it; a cart's mf_ptd / mf_p11
           are still never adopted on their own. */
        if (cur === t && curP === wantP && curPT === wantPT && curD === wantD && cur11 === want11 && curGO === wantGO && curMG === wantMG && okPGA) { return; } /* already correct - never rewrite */
        /* BF v3 session-sticky guard (2026-09-04): a cart that already HOLDS
           ITEMS under a valid arm keeps that arm - this browser adopts it.
           Without this, a fresh visit's fresh deal would rewrite mf_pt and the
           transform would reprice items the shopper added at another price:
           b70 (104) -> a50 (174) is an overcharge, the one direction that is
           never allowed. Empty carts fall through and take the new deal. */
        if (cur && cur !== t && cart.item_count > 0 && Object.prototype.hasOwnProperty.call(ARMS_V3, cur)) {
          adoptArm(cur);
          /* 2026-09-12: do NOT return here. Returning skipped the attribute write, so
             an adopted cart never received mf_go and the grid test lost every
             returning-cart order. Falling through with t = cur writes mf_pt: cur -
             the value already on the cart, so the transform sees no reprice - plus
             whatever else differs (mf_go). The overcharge guard is unchanged. */
          t = cur;
          /* 2026-09-21: the 1+1 gate and the dressing were read from the DEALT arm
             above; adoptArm has just re-derived both from the cart's arm. Re-read
             them, or an adopted q72 cart is written mf_p11 'on' (70% off plus every
             second case free, ~85% off per case) and mf_ptd 'plain'. */
          wantD = ptd();
          want11 = p11();
          if (curP === wantP && curPT === wantPT && curD === wantD && cur11 === want11 && curGO === wantGO && curMG === wantMG && okPGA) { return; }
        }
        /* cheapest-arm-wins adoption REMOVED at the ladder close
           (2026-08-27). The still-valid LOCAL deal is now the only voice:
           while it lives, it re-asserts its own arm unchanged; once it
           expires the browser deals z0 and the stale cart attribute gets
           overwritten - the PDP shows catalog prices by then, so charging
           the old arm would contradict every visible price. Persistent
           carts must not resurrect retired discounts indefinitely. */
        writes++;
        var attrs = { mf_pt: t, mf_p22: wantP, mf_ptp: wantPT, mf_ptd: wantD, mf_p11: want11, mf_go: wantGO, mf_mg: wantMG };
        if (wantPGA) { attrs.mf_pga = wantPGA; }
        return fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: attrs }),
        }).then(function (r) {
          if (!r.ok) { scheduleRetry(); return; }
          return r.json().then(function (after) {
            /* a previous DIFFERENT arm priced a cart with items: re-render */
            if (cur && cart.item_count > 0) {
              try {
                D.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
                  bubbles: true, detail: { cart: after },
                }));
              } catch (e2) {}
            }
          }).catch(function () {});
        });
      }); }).catch(scheduleRetry);
    } catch (e) { scheduleRetry(); }
  }

  /* 2026-10-17 (review XPC-M4): the cart express gate (assets/mf-xpc.js) hides its wallet row while the cart lacks these
     attributes. Asked through this door it gets ONE assert now (the 4 s window reset) instead of waiting for the next
     cart event or a trailing check that a busy page may have used up; the gate asks at most 3 times per page. */
  W.mfPTAssert = function () { lastPost = 0; return assertAttribute(); };

  function boot() {
    /* 2026-10-09 RO POPUP A/B/C (assets/mf-popup-game.js): window.mfPTReady settles when this page's FIRST attribute
       write has landed (at once when there is none), so a prize code applied at page load is written AFTER it and
       never in flight together with it - two concurrent cart writes dropped the code on the dev store (D1). */
    if (!tier()) { W.mfPTReady = Promise.resolve(); return; }

    var firstAssert = assertAttribute();
    W.mfPTReady = Promise.resolve(firstAssert).then(null, function () {});
    decorate();

    /* load-more, drawer re-renders and AJAX grids inject new cards */
    var t = null;
    new MutationObserver(function () {
      if (t) { clearTimeout(t); }
      t = setTimeout(decorate, 400);
    }).observe(D.body, { childList: true, subtree: true });

    D.documentElement.addEventListener('cart:refresh', function () {
      setTimeout(assertAttribute, 1000);
    }, false);

    /* the arm as a step, reported AFTER the cart reconciliation settles: the
       dealt arm can be adopted away by a cheaper cart attribute, and orders
       book on the FINAL arm - the session denominator must match it */
    function reportArm() {
      try {
        if (W.mfTrackPush) {
          W.mfTrackPush('mf_nav_step', { mf_step: 'price:t_' + tier() });
          /* SESSION denominator (2026-09-13). price:t_ is a PAGEVIEW counter, and the
             number of product pages a session views is itself an OUTCOME of the arm -
             so every rate built on it is divided by something the treatment moved.
             Measured on the v7 grid's first hours: 7,45 / 7,90 / 6,79 views per
             session, a 16% spread across arms, while the session-level popup reach
             was balanced. The pageview denominator made a 3-arm difference of p=0,36
             read as a pairwise p=0,035.
             Emitted from HERE, not the dealer, and keyed on the SETTLED arm, because
             a cart adoption can move the arm after the deal and orders book on the
             final one - the denominator has to match the numerator.
             Once per session; a re-deal or an adoption to a different arm re-arms it,
             so a session that changed arms is counted under both and neither loses
             its numerator. Additive: price:t_ keeps its meaning and its history. */
          try {
            var _ptsKey = 'mf_pts';
            if (sessionStorage.getItem(_ptsKey) !== tier()) {
              sessionStorage.setItem(_ptsKey, tier());
              W.mfTrackPush('mf_nav_step', { mf_step: 'pts:' + tier() });
            }
          } catch (ePts) {}
          /* MYSTERY GIFT test sessions, once per session per arm (same reasoning as pts: above); the closed PDP
             test's pdps: step retired with it (2026-10-01) */
          try {
            var _mgA = (W.__mfMG === 'b' || W.__mfMG === 'c') ? W.__mfMG : 'a';
            if (sessionStorage.getItem('mf_mgs') !== _mgA) {
              sessionStorage.setItem('mf_mgs', _mgA);
              W.mfTrackPush('mf_nav_step', { mf_step: 'mgs:' + _mgA });
            }
          } catch (eMgs) {}
          /* C/D PRODUCT-PAGE TEST sessions (2026-10-07, Termékoldal = 'cd'): pfs:c / pfs:d once per session per arm,
             on EVERY page, for everyone dealt (intent to treat) - the closed 09-22 PDP test lost upstream, through
             fewer PDP views, which a PDP-view denominator cannot see. __mfPF is absent outside 'cd' mode and 'x'
             (excluded) sends nothing, so this is silent unless the test runs. */
          try {
            var _pf = W.__mfPF;
            if ((_pf === 'c' || _pf === 'd') && sessionStorage.getItem('mf_pfs') !== _pf) {
              sessionStorage.setItem('mf_pfs', _pf);
              W.mfTrackPush('mf_nav_step', { mf_step: 'pfs:' + _pf });
            }
          } catch (ePfs) {}
          /* RO POPUP A/B/C sessions (2026-10-09, CONTRACT.md): pgs:a / pgs:b / pgs:c / pgs:s / pgs:x once per session
             per arm, on EVERY page, for everyone the dealer saw (s and x included, so the exclusions are counted too).
             window.__mfPG is absent while the test is 'ki', so this is silent outside the test.
             2026-10-14 (CONTRACT UPDATE 7): pgs:p for the percent arm of test 'cp'. */
          try {
            var _pg = W.__mfPG;
            if (/^[abcpsx]$/.test(_pg || '') && sessionStorage.getItem('mf_pgs') !== _pg) {
              sessionStorage.setItem('mf_pgs', _pg);
              W.mfTrackPush('mf_nav_step', { mf_step: 'pgs:' + _pg });
            }
          } catch (ePgs) {}
          /* RO POPUP A/B/C visitors (CONTRACT UPDATE 2): pgv:ARM ONCE PER VISITOR - the per-visitor denominator, since
             arm c brings visitors back and inflates its own per-session count. The flag is n: 1 in the dealer's
             localStorage mf_pg1 (the dealer keeps it while the arm stays the same), set right after the push. x has
             no working localStorage by definition, so x falls back to once per session (sessionStorage mf_pgv). */
          try {
            var _pv = W.__mfPG;
            if (/^[abcps]$/.test(_pv || '')) {
              var _r1 = JSON.parse(localStorage.getItem('mf_pg1') || 'null');
              if (_r1 && typeof _r1 === 'object' && _r1.a === _pv && _r1.n !== 1) {
                W.mfTrackPush('mf_nav_step', { mf_step: 'pgv:' + _pv });
                _r1.n = 1;
                localStorage.setItem('mf_pg1', JSON.stringify(_r1));
              }
            } else if (_pv === 'x' && sessionStorage.getItem('mf_pgv') !== 'x') {
              sessionStorage.setItem('mf_pgv', 'x');
              W.mfTrackPush('mf_nav_step', { mf_step: 'pgv:x' });
            }
          } catch (ePgv) {}
          W.mfTrackPush('mf_nav_step', { mf_step: 'promo:t_' + p22() });
          W.mfTrackPush('mf_nav_step', { mf_step: 'p11:' + p11() });
          W.mfTrackPush('mf_nav_step', { mf_step: 'cmb:' + tier() + '_' + p22() });
          W.mfTrackPush('mf_nav_step', { mf_step: 'price:tp_' + ptier() });
          W.mfTrackPush('mf_nav_step', { mf_step: 'pres:' + ptd() });
          W.mfTrackPush('mf_nav_step', { mf_step: 'cmb3:' + tier() + '_' + ptd() });
          /* go:<arm> and gb:<badge> are NOT pushed: both are marginals of the gbo:
             cell (sum over the other coin) and the 32-step cap is tight on a deep
             PLP pageview. Derive them in the read. */
          if (goa()) {
            W.mfTrackPush('mf_nav_step', { mf_step: 'gcmb:' + tier() + '_' + goa() });
            W.mfTrackPush('mf_nav_step', { mf_step: 'gbo:' + goa() + '_' + gob() });
          }
          /* case-PDP price band (set by product-rhode): does the sale face
             work harder where the badge percent is bigger? */
          if (W.__mfPdpPB === 'lo' || W.__mfPdpPB === 'mid' || W.__mfPdpPB === 'hi') {
            W.mfTrackPush('mf_nav_step', { mf_step: 'prb:' + W.__mfPdpPB + '_' + ptd() });
          }
        }
      } catch (e) {}
    }
    var settled = false;
    function reportOnce() { if (!settled) { settled = true; reportArm(); } }
    Promise.resolve(firstAssert).catch(function () {}).then(reportOnce);
    setTimeout(reportOnce, 3000); /* network hangs must not lose the pageview */
  }

  /* defer scripts run before DOMContentLoaded; waiting for it guarantees
     mf-track (a later defer) has defined mfTrackPush - same fix as promo22 */
  if (D.readyState === 'complete') { boot(); }
  else { D.addEventListener('DOMContentLoaded', boot, false); }
})();
